import {
  SearchData,
  SearchResultItemWithType,
  RecentSearch,
} from '../../../common/types/search'
import { RecentSearches } from '../../../common/components/RecentSearches'
import { SearchLoading } from '../../../common/components/popover/SearchLoading'
import { SearchResults } from '../SearchResults'
import { AllResultsButton } from '../../../common/components/AllResultsButton'
import { hasAnyResults } from '../../helpers/searchUtils'
import { UserFull, Group } from '@wellesley/openapi'
import { SeeMoreResultsOnSearchPage } from '../SeeMoreResultsOnSearchPage'

type SearchResultsRendererProps = {
  loading: boolean
  mainData?: SearchData
  elsewhereData?: SearchData
  recentSearches: RecentSearch[]
  showElsewhereResults: boolean
  searchInput: string
  groupName?: string
  onRecentSearchClick: (search: RecentSearch) => void
  onSuggestionClick: (item: SearchResultItemWithType) => void
  onAllResultsClick: () => void
}

const distributeUsersAndGroups = ({
  users,
  groups,
  tags,
  maxTotal = 8,
}: {
  users: UserFull[]
  groups: Group[]
  tags: string[]
  maxTotal: number
}) => {
  const tagCount = tags.length
  const userCount = users.length
  const groupCount = groups.length

  const tagsToShow = Math.min(tagCount, 4)
  const remainingSlots = maxTotal - tagsToShow

  const totalUsersAndGroups = userCount + groupCount

  if (totalUsersAndGroups <= remainingSlots) {
    return {
      tags: tags.slice(0, tagsToShow),
      users: users,
      groups: groups,
    }
  }

  let usersToShow = Math.floor(remainingSlots / 2)
  let groupsToShow = Math.floor(remainingSlots / 2)

  if (userCount < usersToShow) {
    usersToShow = userCount
    groupsToShow = Math.min(remainingSlots - userCount, groupCount)
  } else if (groupCount < groupsToShow) {
    groupsToShow = groupCount
    usersToShow = Math.min(remainingSlots - groupCount, userCount)
  }

  return {
    tags: tags.slice(0, tagsToShow),
    users: users.slice(0, usersToShow),
    groups: groups.slice(0, groupsToShow),
  }
}

export const SearchResultsRenderer = ({
  loading,
  mainData,
  groupName,
  elsewhereData,
  showElsewhereResults,
  recentSearches,
  searchInput,
  onRecentSearchClick,
  onSuggestionClick,
  onAllResultsClick,
}: SearchResultsRendererProps) => {
  if (!searchInput?.length) {
    return (
      <RecentSearches
        searches={recentSearches.slice(0, 8)}
        onSearchClick={onRecentSearchClick}
      />
    )
  }

  if (loading) {
    return <SearchLoading />
  }

  if (!mainData) return null

  const hasMainResults = hasAnyResults(mainData)
  const hasElsewhereResults = hasAnyResults(elsewhereData)

  if (!hasMainResults && !hasElsewhereResults) {
    return <SeeMoreResultsOnSearchPage onAllResultsClick={onAllResultsClick} />
  }

  if (hasElsewhereResults && showElsewhereResults) {
    const distributedElsewhereData = distributeUsersAndGroups({
      users: elsewhereData?.users || [],
      groups: elsewhereData?.groups || [],
      tags: elsewhereData?.tags || [],
      maxTotal: 8,
    })
    return (
      <>
        <SeeMoreResultsOnSearchPage onAllResultsClick={onAllResultsClick} />
        <h3 className="pt-m text-lg font-semibold text-gray-900">
          Found outside this group
        </h3>
        <AllResultsButton onAllResultsClick={onAllResultsClick} />
        <SearchResults
          searchInput={searchInput}
          data={distributedElsewhereData}
          onItemClick={onSuggestionClick}
        />
      </>
    )
  }

  const searchResults = distributeUsersAndGroups({
    users: mainData?.users || [],
    groups: mainData?.groups || [],
    tags: mainData?.tags || [],
    maxTotal: 8,
  })

  return (
    <>
      <AllResultsButton onAllResultsClick={onAllResultsClick} />
      <SearchResults
        searchInput={searchInput}
        data={searchResults}
        onItemClick={onSuggestionClick}
        groupName={groupName}
      />
    </>
  )
}
